import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Wrench, CheckCircle2, Gauge } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useTranslation, Trans } from 'react-i18next';

const Home = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[450px] sm:h-[550px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1694039446022-2d227e8b104b?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F44]/95 via-[#0A1F44]/90 to-[#0A1F44]/70" />
        </div>

        <div className="relative z-10 max-w-[95%] sm:max-w-[90%] md:max-w-6xl mx-auto text-center px-4 sm:px-6">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            <Trans i18nKey="home.hero.title" components={{ 1: <span className="text-[#FF6A00]" />, br: <br /> }} />
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed">
            <Trans i18nKey="home.hero.description" />
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/products" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-[#FF6A00] hover:bg-[#E55F00] text-white transition-all duration-300 transform hover:scale-105"
              >
                {t('home.hero.explore')} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact" className="w-full sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-[#0A1F44] transition-all duration-300"
              >
                {t('home.hero.contact')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-[95%] sm:max-w-[90%] md:max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1F44] mb-3 sm:mb-4">
              {t('home.features.title')}
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {t('home.features.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              {
                icon: Gauge,
                title: t('home.features.certifiedTools.title'),
                desc: t('home.features.certifiedTools.desc'),
              },
              {
                icon: Wrench,
                title: t('home.features.localSupport.title'),
                desc: t('home.features.localSupport.desc'),
              },
              {
                icon: ShieldCheck,
                title: t('home.features.verifiedPerformance.title'),
                desc: t('home.features.verifiedPerformance.desc'),
              },
              {
                icon: CheckCircle2,
                title: t('home.features.directPartnership.title'),
                desc: t('home.features.directPartnership.desc'),
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <Card
                  key={i}
                  className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  <CardContent className="pt-8 pb-6 px-4 sm:px-6 text-center">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#FF6A00]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-7 w-7 sm:h-8 sm:w-8 text-[#FF6A00]" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-[#0A1F44] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                      {item.desc}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-[#0A1F44] text-white text-center px-4 sm:px-6">
        <div className="max-w-[95%] sm:max-w-[90%] md:max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6">
            {t('home.cta.title')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-8 leading-relaxed">
            {t('home.cta.description')}
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#FF6A00] hover:bg-[#E55F00] text-white transition-all duration-300 transform hover:scale-105 px-6 py-3 text-sm sm:text-base"
            >
              {t('home.cta.button')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
